import * as React from 'react';
import {
  Type,
  Types,
  TypeImplementation,
  getSystem,
} from '@packages/systems/wfdl';
import {
  EList,
  EText,
  TList,
  TTypeApplication,
  TTypeConstructor,
  TTypeReference,
  unionTypeName,
} from '@packages/systems/wfdl-ast';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {pluginName} from '../constants';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ContextualExpressions,
  getAnn,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {Autocomplete} from '@packages/systems/spring';
import {TAnyOfListItem} from './AnyOfListItem';

export const TOptionAnyOfConditionValue = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(
      qualify(pluginName, typeName('OptionAnyOfConditionValue'))
    ),
    arg,
  });

export const OptionAnyOfListItemType = TypeImplementation.Alias({
  params: ['optionUnion'],
  type: TAnyOfListItem(TTypeReference('optionUnion')),
  render: () => null,
  renderSummary: function OptionAnyOfListItemSummaryRender({expr}) {
    const value = ContextualExpressions.getText(stripAnn(expr));
    const type = getAnn(expr)?.type;
    const options = React.useMemo(() => {
      return type ? getUnionOptions(type) : [];
    }, [type]);
    const matchingOption = options.find((option) => option.value === value);
    if (matchingOption) {
      return <span>{matchingOption.label}</span>;
    }
    return <span>Deleted value</span>;
  },
});

/**
 * Type used to represent multiple CMS Option field options used for is any and
 * is not any conditions both for Option field predicates.
 */
export const OptionAnyOfConditionValueType = TypeImplementation.Alias({
  params: ['optionUnion'],
  type: TList(
    TTypeApplication({
      con: TTypeConstructor(
        qualify(pluginName, typeName('OptionAnyOfListItem'))
      ),
      arg: TTypeReference('optionUnion'),
    })
  ),
  /**
   * Autocomplete that renders a list of options to select from.
   * `[(Tech | x), (Design | x) |v]`
   */
  render: function OptionAnyOfConditionValueRender({expr, dispatch}) {
    const type = getAnn(expr)?.type;

    const inputValue = React.useMemo(() => {
      const list = ContextualExpressions.getList(stripAnn(expr));
      if (list) {
        return list.reduce<string[]>((acc, item) => {
          const text = ContextualExpressions.getText(stripAnn(item));
          if (text) {
            acc.push(text);
          }
          return acc;
        }, []);
      }
      return [];
    }, [expr]);

    const options = React.useMemo(() => {
      const listItemType = type ? Types.getListItemType(type) : null;
      return listItemType ? getUnionOptions(listItemType) : [];
    }, [type]);

    const onChange = React.useCallback(
      (nextValue: string[]) => {
        const newExpr = EList(nextValue.map((item) => EText(item)));
        dispatch(updateExpressionAction(newExpr));
      },
      [dispatch]
    );

    return (
      <Autocomplete
        aria-label="Select options"
        data-automation-id="cms-option-any-of-input"
        value={inputValue}
        selectionMode="multiple"
        options={options}
        onChange={onChange}
        getSelectedOptionByValue={getDeletedValueOption}
      />
    );
  },
});

/**
 * Ensures deleted options have a 'Deleted value' tag in the autocomplete input
 */
function getDeletedValueOption(value: string) {
  return {
    label: 'Deleted value',
    value,
  };
}

function getUnionOptions(
  anyOfListValueType: Type
): {label: string; value: string}[] {
  const outerTyApp = Types.deconstructTypeApplication(anyOfListValueType);
  const optionUnionType =
    outerTyApp && outerTyApp.args.length === 1 && outerTyApp.args[0]
      ? outerTyApp.args[0]
      : null;

  if (optionUnionType) {
    const unionTyApp = Types.deconstructTypeApplication(optionUnionType);
    if (unionTyApp && namesEqual(unionTyApp.con, unionTypeName)) {
      const maybeRowType = unionTyApp.args[0];
      if (unionTyApp.args.length === 1 && maybeRowType) {
        return getUnionTagsAndLabelsFromRowType(maybeRowType);
      }
    }
  }

  return [];
}

function getUnionTagsAndLabelsFromRowType(
  maybeRowType: Type
): {label: string; value: string}[] {
  const row = Types.getRowCons(maybeRowType);
  if (row) {
    const externalLabel = getSystem(maybeRowType)?.label;
    return [
      {label: externalLabel || row.label, value: row.label},
      ...getUnionTagsAndLabelsFromRowType(row.tail),
    ];
  }
  return [];
}
